// All images for this page are served from S3 behind CloudFront rather than
// bundled as local static assets, matching how the revamp repo serves them.
// Update PARTNER_AWS_ASSETS_URL if the bucket/distribution changes.
const ASSETS_BASE_URL =
  process.env.PARTNER_AWS_ASSETS_URL ||
  'https://d1zxacqtu8lvf8.cloudfront.net/partner-aws'

const asset = (filename) => `${ASSETS_BASE_URL}/${filename}`

export const PARTNER_AWS_SOLUTION_SECTION_ID = 'partner-aws-solution'

export const awsPartnerBadge = asset('aws-partner-badge.png')
export const heroBackgroundImage = asset('hero-bg.jpg')

export const useCaseIllustrations = [
  asset('use-case-illustration.png'),
  asset('use-case-illustration-2.png'),
]

export const certificationBadgeImages = [
  asset('cert-solutions-architect-professional.png'),
  asset('cert-security-specialty.png'),
  asset('cert-data-engineer-associate.png'),
  asset('cert-solutions-architect-professional-2.png'),
  asset('cert-ai-practitioner-foundational.png'),
  asset('cert-cloud-practitioner-foundational.png'),
]

// Whole-card exports from Figma (node 715:17451), cropped to the card box with
// the baked drop shadow removed - the CSS box-shadow supplies that. Used
// instead of rebuilding the card chrome in SCSS.
export const solutionCardImages = [
  asset('solution-card-data-to-chat.svg'),
  asset('solution-card-data-to-dashboard.svg'),
  asset('solution-card-data-platform.svg'),
  // ?v=2 because this key was overwritten after being served with a 30-day
  // max-age: a CloudFront invalidation clears the CDN but not browsers that
  // already cached the old card. The query makes it a new URL for them.
  `${asset('solution-card-mlops.svg')}?v=2`,
  asset('solution-card-cloud-migration.svg'),
  asset('solution-card-security.svg'),
]

// Figma exports every card at this size; cardStyle scales them fluidly.
export const SOLUTION_CARD_WIDTH = 394.667
export const SOLUTION_CARD_HEIGHT = 438

// Whole-icon exports from Figma (717:19192 / 19204 / 19216 / 19229 / 19241 /
// 19253): the 64x64 badge plus its glow, which the filter spreads out to a
// 148x148 box with the badge sitting at offset (42, 40) inside it.
//
// Rasterised at 2x rather than served as SVG. The Figma icons stack an
// feMorphology dilate with three feGaussianBlurs, and WebKit composites that
// noticeably brighter than Blink, so the glow looked blown out in Safari.
// A bitmap renders identically everywhere. They are flattened onto opaque
// black to keep the files small (102KB for six, vs 271KB with alpha) - which
// is invisible only because this section's background is exactly #000. If that
// background ever changes, these need re-exporting with transparency.
export const capabilityIcons = [
  asset('capability-icon-data-engineer.png'),
  asset('capability-icon-analytics.png'),
  asset('capability-icon-mlops.png'),
  asset('capability-icon-genai.png'),
  asset('capability-icon-cloud-migration.png'),
  asset('capability-icon-security.png'),
]

export const CAPABILITY_ICON_SIZE = 148

export const architectureStepIcons = [
  asset('icon-step-source.svg'),
  asset('icon-step-ingest.svg'),
  asset('icon-step-warehouse.svg'),
  asset('icon-step-ml.svg'),
  asset('icon-step-genai.svg'),
  asset('icon-step-app.svg'),
]

// Index-zipped with industry.list in the locale, so these must stay in the
// same order as that list: Financial Services, Manufacturing, Public Sector.
export const industryImages = [
  asset('industry-finance-2.jpg'),
  asset('industry-manufacturing-2.jpg'),
  asset('industry-government-2.jpg'),
]

export const industryImagePositions = ['center', 'center', 'center 58%']

export const bulletCheckIcon = asset('bullet-check.svg')

// The architecture caption is used as a Figma export rather than gradient text.
// It is the only section caption that is a standalone text node in the design -
// the other five live inside Section header instances, which can only be
// exported together with their <h2>, so those stay as CSS text.
export const howWeWorkCaption = asset('caption-how-we-work.svg')
export const HOW_WE_WORK_CAPTION_WIDTH = 136
export const HOW_WE_WORK_CAPTION_HEIGHT = 15

export const CONTACT_URL = '/contact'

// Footer icons exported from Figma (721:20173).
export const footerIcons = {
  call: asset('footer-icon-call.svg'),
  mail: asset('footer-icon-mail.svg'),
}

export const footerSocials = [
  {
    key: 'facebook',
    label: 'Facebook',
    href: 'https://www.facebook.com/datawowai/',
    icon: asset('footer-social-facebook.svg'),
  },
  {
    key: 'linkedin',
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/datawowio/',
    icon: asset('footer-social-linkedin.svg'),
  },
  {
    key: 'youtube',
    label: 'YouTube',
    href: 'https://www.youtube.com/@datawowio/',
    icon: asset('footer-social-youtube.svg'),
  },
  {
    key: 'tiktok',
    label: 'TikTok',
    href: 'https://www.tiktok.com/@datawow/',
    icon: asset('footer-social-tiktok.svg'),
  },
]

// The two BSI marks already ship with this repo as vectors, so they are reused
// rather than re-uploading the design's PNGs.
//
// The third mark is named "BSI-Logo / 27701" in Figma but is actually the AWS
// Partner badge - the layer looks like a duplicated-and-renamed BSI one. It has
// no vector data (it is an image fill), and the artwork ships dark-on-
// transparent, which is invisible on this black footer; the design displays it
// white. So it is recoloured to white, keeping the alpha, and uploaded.
export const footerCertifications = [
  {src: '/images/iso-ism.svg', alt: 'BSI ISO/IEC 27001 certified'},
  {src: '/images/iso-pim.svg', alt: 'BSI ISO/IEC 27701 certified'},
  {
    src: asset('footer-aws-partner-badge.png'),
    alt: 'AWS Partner - Advanced Tier Services',
  },
]

// The locale values carry <br/> markers (converted from the source repo's \n),
// so anything that can wrap onto a designed line break is rendered as HTML.
export const html = (value) => ({dangerouslySetInnerHTML: {__html: value}})

export const zipWithImage = (items, images) =>
  (items || []).map((item, index) => ({...item, image: images[index] || ''}))
